import type { Lesson } from '../schema.js';

// Pirate Bridge lessons: explain + quiz only. The facts match the game as built (packages/game-engine/src/pirate/):
// four players each playing for themselves, hands shrink and grow along a ladder (the full deck deals 13 down to 1 and
// then 2 back up to 13 — 25 rounds; the Big Cards deck deals 7 down to 1 and back to 7 — 13 rounds), a turned-up card
// names the trump suit, everyone secretly guesses their tricks and the guesses stay hidden until all four are locked,
// you must follow suit and the highest card of the led suit wins unless someone plays trump, an exact guess scores
// 10 plus the tricks you took and any miss (over OR under) scores nothing, a round where nobody is exact scores
// nothing for anyone, the highest total wins and a tie shares the win. Cap'n's Hat is the optional 1-card round played
// with the card on your forehead: you see the other three cards, never your own, guess, and then play it blind.
// These rules are recalled, not checked against a rulebook, and a player should confirm them.
export const PIRATE_LESSONS: Lesson[] = [
  {
    id: 'pirate-guess',
    slug: 'pirate-guess-your-tricks',
    title: 'Pirate Bridge: guess your tricks',
    audienceLevel: 'beginner',
    concepts: ['pirate'],
    steps: [
      {
        kind: 'explain',
        title: 'Everyone guesses, then the tricks are played',
        body: 'Pirate Bridge is a guessing game with cards. Four players each play for themselves. Before the tricks are played, every player secretly guesses how many tricks they will win — and the guesses stay hidden until all four are locked in, so you cannot copy anyone. Then the cards are played one at a time: you must follow the suit that was led if you can, and the highest card of that suit takes the trick. But a card of the trump suit beats every other suit! Before each deal, one card is turned up to show which suit is trump.',
        narration: 'Guess how many tricks you will win, keep it secret, then follow suit — and watch out for trump.',
      },
      {
        kind: 'quiz',
        title: 'Making your guess',
        body: 'Time for a quick check!',
        questions: [
          {
            prompt: 'When can the other players see your guess?',
            choices: ['After all four players have locked theirs in', 'As soon as you lock it in', 'After the first trick'],
            correct: 0,
          },
          {
            prompt: 'A heart is led. You have no hearts, but you do have the trump suit. What can you do?',
            choices: ['Play a trump card — trump beats the led suit', 'You must pass your turn', 'You must play your highest card of any suit'],
            correct: 0,
          },
          {
            prompt: 'How do you know which suit is trump?',
            choices: ['A card is turned up before the round', 'The dealer picks it', 'The suit of the first card played'],
            correct: 0,
          },
        ],
      },
    ],
  },
  {
    id: 'pirate-scoring',
    slug: 'pirate-exact-or-nothing',
    title: 'Pirate Bridge: exact or nothing',
    audienceLevel: 'beginner',
    concepts: ['pirate'],
    steps: [
      {
        kind: 'explain',
        title: 'Only an exact guess scores',
        body: 'Winning lots of tricks is not the goal — guessing EXACTLY is. If your guess is exactly right, you score 10 points plus one for every trick you took. If you are off by even one — too high or too low — you score nothing for that round. And if nobody guesses exactly, nobody scores at all: that happens more than you would think! The hands get smaller and then bigger again, following a ladder: thirteen cards down to one, then back up to thirteen. After the whole ladder, the player with the highest total wins — and if two players tie, they share the win.',
        narration: 'An exact guess scores ten plus your tricks. One off scores nothing.',
      },
      {
        kind: 'quiz',
        title: 'Scoring a round',
        body: 'Time for a quick check!',
        questions: [
          {
            prompt: 'You guessed 3 tricks and won exactly 3. What do you score?',
            choices: ['13 (10 plus one per trick)', '3', '10'],
            correct: 0,
          },
          {
            prompt: 'You guessed 4 tricks but won only 2. What do you score?',
            choices: ['Nothing', '2', '4'],
            correct: 0,
          },
          {
            prompt: 'Nobody guessed exactly. Who scores?',
            choices: ['Nobody', 'The player who won the most tricks', 'The dealer'],
            correct: 0,
          },
          {
            prompt: 'Two players tie for the highest total at the end. What happens?',
            choices: ['They share the win', 'They play one more full game', 'The dealer wins'],
            correct: 0,
          },
        ],
      },
    ],
  },
  {
    id: 'pirate-hat',
    slug: 'pirate-captains-hat-and-big-cards',
    title: "Pirate Bridge: Cap'n's Hat and Big Cards",
    audienceLevel: 'beginner',
    concepts: ['pirate'],
    steps: [
      {
        kind: 'explain',
        title: "The strangest round of all: Cap'n's Hat",
        body: "The ladder's one-card round can be played a special way: Cap'n's Hat. Everyone holds their single card on their forehead, so you can see the other three players' cards — but never your own! You still guess how many tricks you will win (with a card you cannot see), and then you play it blind. It is a round of pure detective work: the cards you CAN see tell you what your own card probably is. You can also turn on Big Cards, a smaller deck of just 28 cards, which makes every round quicker — the ladder runs seven down to one and back up again.",
        narration: "On your forehead goes the card: see everyone else's, guess, and play yours blind.",
      },
      {
        kind: 'quiz',
        title: 'The hat round',
        body: 'Time for a quick check!',
        questions: [
          {
            prompt: "In Cap'n's Hat, whose card can you see?",
            choices: ["Everyone else's — but not your own", 'Only your own', "All four cards, including your own"],
            correct: 0,
          },
          {
            prompt: 'How do you guess how many tricks you will win with a card you cannot see?',
            choices: ['By studying the three cards you can see', 'You skip the guess', 'The other players tell you'],
            correct: 0,
          },
          {
            prompt: 'How many cards are in a Big Cards deck?',
            choices: ['28', '52', '13'],
            correct: 0,
          },
        ],
      },
    ],
  },
];
